"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
export default function ThePillars() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const [flippedCards, setFlippedCards] = useState<boolean[]>(new Array(9).fill(false));
  const handleCardClick = (index: number) => () => {
    setFlippedCards(prev => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };
  const handleScrollToSecureLegacy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const element = document.getElementById('secure-legacy');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const pillars = [{
    id: "marriage",
    numeral: "I",
    title: "MARRIAGE",
    subtitle: "THE LOVE & THE LOYALTY",
    description: "This pillar chronicles the partnership that started it all—the shared values, the mutual support, and the commitment that built your foundation. It serves as a guide for future generations, providing a blueprint for how to cultivate a lasting relationship based on respect and shared purpose."
  }, {
    id: "parenting",
    numeral: "II",
    title: "PARENTING",
    subtitle: "THE GUIDANCE & THE GROWTH",
    description: "This pillar details the journey of raising the next generation—the intentional choices and the lessons learned along the way. It offers a clear roadmap for future parents, documenting the dedication and values required to nurture a strong, unified family and a lasting lineage."
  }, {
    id: "career",
    numeral: "III",
    title: "CAREER",
    subtitle: "THE CHOICES & THE CONSEQUENCES",
    description: "This pillar chronicles the professional journey—from the early risks taken to the strategic decisions that defined your path. By documenting both the challenges and the results of a dedicated working life, it equips future generations with the insight to navigate their own careers with accountability and purpose."
  }, {
    id: "prosperity",
    numeral: "IV",
    title: "SUCCESS",
    subtitle: "THE EFFORTS & THE REWARDS",
    description: "Success is the result of dedication and clear vision. This pillar shares the professional journey, the risks taken, and the milestones achieved. It provides a blueprint of achievement for future generations to build upon and define their own path."
  }, {
    id: "community",
    numeral: "V",
    title: "COMMUNITY",
    subtitle: "THE SUPPORT & THE SERVICE",
    description: "No family thrives in isolation. This pillar records the network of support that sustained your journey and the service you provided to those around you. It teaches future generations the value of building strong communal bonds and the responsibility of contributing to the well-being of others."
  }, {
    id: "tradition",
    numeral: "VI",
    title: "TRADITION",
    subtitle: "THE VALUES & THE HERITAGE",
    description: "Family identity is anchored by shared principles and stability. This pillar details the values and convictions that have grounded your home and shaped your lineage. It serves as a cultural compass for future generations, helping them remain connected to their roots and the standards that define this family."
  }, {
    id: "fortitude",
    numeral: "VII",
    title: "RESILIENCE",
    subtitle: "THE TRIALS & THE VICTORY",
    description: "True strength is forged through challenges. This pillar documents the obstacles you overcame, the decisions that shaped your path, and the persistence that moved the family forward. It serves as a practical guide for the generations who will face their own tests."
  }, {
    id: "spirituality",
    numeral: "VIII",
    title: "SPIRITUALITY",
    subtitle: "THE FAITH & THE FOUNDATION",
    description: "Spirituality provides the ultimate perspective. This pillar records the faith that has guided your journey and the spiritual principles that offer stability in an ever-changing world. It serves as a permanent foundation for future generations, anchoring them in truth and providing a clear sense of purpose."
  }, {
    id: "ancestry",
    numeral: "IX",
    title: "ANCESTRY",
    subtitle: "THE ROOTS & THE REACH",
    description: "This pillar chronicles the history inherited from our ancestors. It serves as a vital record of our origins, showing how the strength of our past supports the reach of our future and providing the next generation with a clear sense of belonging."
  }];
  return <section id="the-pillars" className="bg-light-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div ref={headlineRef} className={`text-center mb-12 transition-all duration-700 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="inline-block bg-dark-background text-accent font-[var(--font-cinzel)] tracking-[4px] mb-6 px-8 py-4 rounded-lg !font-bold !text-[40px]">The 'Nine Life Pillars' Framework</h2>
          <h3 className="text-[#D4AF37] italic tracking-wide text-center pb-[10px] font-[var(--font-cinzel)] text-xl !font-sans !font-semibold sm:text-2xl">The architectural blueprint for a definitive family wisdom legacy.</h3>
          <p className="text-foreground-light font-[var(--font-libre-baskerville)] text-lg leading-relaxed max-w-3xl mx-auto"><span style={{
            color: 'rgb(0, 31, 61)',
            fontSize: '20px'
          }}>Your life is a singular, complex tapestry of trials, triumphs, and truth. I curate your&nbsp;</span><span data-path-to-node="10,0" data-index-in-node="90" data-ast-id="f:/src/pages/Home/sections/TheTiers.tsx|sig:qnt7op" data-ast-start="4579" data-ast-end="4681" style={{
            fontWeight: 'bolder',
            color: 'rgb(0, 31, 61)',
            fontSize: '20px'
          }}>singular journey across nine life pillars</span><span style={{
            color: 'rgb(0, 31, 61)',
            fontSize: '20px'
          }}>, ensuring your authentic wisdom is preserved as a permanent foundation for the generations to follow.</span></p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => <div key={pillar.id} id={pillar.id} data-index={i} className={`h-72 cursor-pointer transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          perspective: "1000px",
          transitionDelay: `${i * 80}ms`
        }} onClick={handleCardClick(i)}>
              <div className="relative w-full h-full transition-transform duration-[600ms] ease-[var(--ease-out-back)]" style={{
            transformStyle: "preserve-3d",
            transform: flippedCards[i] ? "rotateY(180deg)" : "rotateY(0deg)"
          }}>
                {}
                <div className="absolute inset-0 w-full h-full rounded-xl border border-[hsl(45,65%,52%)] shadow-soft overflow-hidden" style={{
              backfaceVisibility: "hidden"
            }}>
                  <div className="absolute inset-0 bg-[#002147]" />
                  <div className="absolute inset-0 opacity-10 bg-repeat" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }} />
                  <div className="relative h-full p-8 flex flex-col items-center justify-between">
                    <div className="font-[var(--font-cinzel)] text-xl font-bold text-[hsl(45,65%,52%)]">
                      {pillar.numeral}
                    </div>
                    <div className="flex flex-col items-center gap-3">
                      <h3 className="font-[var(--font-cinzel)] text-2xl font-bold text-[hsl(45,65%,52%)] text-center leading-tight">
                        {pillar.title}
                      </h3>
                      <p className="font-[var(--font-montserrat)] text-xs font-medium text-white uppercase tracking-[2px] text-center">
                        {pillar.subtitle}
                      </p>
                    </div>
                    <p className="font-[var(--font-montserrat)] text-sm font-light text-[hsl(45,65%,52%)] tracking-[1px] text-center">
                      Click here
                    </p>
                  </div>
                </div>

                {}
                <div className="absolute inset-0 w-full h-full rounded-xl bg-[hsl(45,65%,52%)] border border-[hsl(45,65%,52%)] shadow-medium p-6 flex flex-col items-center justify-center gap-4" style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)"
            }}>
                  <p className="font-[var(--font-libre-baskerville)] text-sm leading-relaxed text-[#002147] text-center">
                    {pillar.description}
                  </p>
                  <button onClick={handleScrollToSecureLegacy} className="mt-2 bg-[#001F3F] text-[#FDFDF5] font-[var(--font-montserrat)] font-medium text-xs tracking-[1px] px-4 py-2 rounded-md shadow-soft hover:bg-[hsl(210,100%,16%)] hover:shadow-medium hover:scale-[1.02] active:bg-[hsl(210,100%,20%)] transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FDFDF5]/30">
                    ARCHITECT YOUR {pillar.title} LEGACY
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}